import { useTheme } from '@emotion/react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    styled,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { ContentState, convertFromHTML, Editor, EditorState, Modifier } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { useContext, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import googleImg from '../../../../src/assets/googleImage.png';
import googleImgSmall from '../../../../src/assets/googleSmall.png';
import { Context, REQUIRED_RED } from '../../../App';
import {
    coreSettingsKeys,
    useGetEmailThankYouDetails,
    useSendTestThankYouEmail,
    useUpdateEmailThankYouDetails,
    useUpdateShopConfig,
} from '../../../services/core/settings/settings.hooks';
import { APPOINTMENT_REVIEW_PLATFORM } from '../../../utility/Constant';

import { Chip } from '@mui/material';
import SuccessMessageModal from '../../Common/SuccessMessage';

import { toast } from 'sonner';
import { MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';
import queryClient from '../../../config/queryClient';
import './EmailCustomer.css';

const TextIcon = ({ color = '#000' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M16.1445 2.5H4.14453C3.31728 2.5 2.64453 3.18068 2.64453 4.01768V13.1238C2.64453 13.9608 3.31728 14.6414 4.14453 14.6414H6.39453V17.5L11.1023 14.6414H16.1445C16.9718 14.6414 17.6445 13.9608 17.6445 13.1238V4.01768C17.6445 3.18068 16.9718 2.5 16.1445 2.5ZM16.1445 13.1238H10.6868L7.89453 14.8183V13.1238H4.14453V4.01768H16.1445V13.1238Z"
            fill={color}
            fillOpacity="0.87"
        />
    </svg>
);

const EmailIcon = ({ color = '#000' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M6.25 5L11.875 8.75L17.5 5M2.5 10.625H8.75M3.75 13.125H10M5.625 15.625H18.125V4.375H5.625V8.125H1.25"
            stroke={color}
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
    </svg>
);

const AppIcon = ({ color = '#000' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <g clipPath="url(#clip0)">
            <path
                d="M13.8889 2.22266H6.11111C5.81643 2.22266 5.53381 2.33972 5.32544 2.54809C5.11706 2.75647 5 3.03908 5 3.33377V16.6671C5 16.9618 5.11706 17.2444 5.32544 17.4528C5.53381 17.6611 5.81643 17.7782 6.11111 17.7782H13.8889C14.1836 17.7782 14.4662 17.6611 14.6746 17.4528C14.8829 17.2444 15 16.9618 15 16.6671V3.33377C15 3.03908 14.8829 2.75647 14.6746 2.54809C14.4662 2.33972 14.1836 2.22266 13.8889 2.22266ZM10.5556 16.6671H9.44444V15.556H10.5556V16.6671ZM6.11111 14.4449V3.33377H13.8889V14.4449H6.11111Z"
                fill={color}
            />
        </g>
    </svg>
);
const ChipWrapper = styled('div')({
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
});

const StyledChip = styled(Chip)(({ $selected }) => ({
    height: '36px',
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    paddingLeft: 12,
    paddingRight: 12,

    ...($selected
        ? {
              borderRadius: '50px',
              border: '1px solid #0066E1',
              backgroundColor: '#007AFF',
              boxShadow: '0 2px 2px 0 rgba(0, 122, 255, 0.25)',
              color: '#FFF',
              '&:hover': { backgroundColor: '#007AFF', borderColor: '#0066E1' },
          }
        : {
              borderRadius: '100px',
              border: '1px solid #DCDCDC',
              backgroundColor: '#FFF',
              boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
              color: '#000',
              '&:hover': { backgroundColor: '#FFF', borderColor: '#DCDCDC' },
          }),
}));

const FilterIcon = ({ color }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="20" viewBox="0 0 23 20" fill="none">
        <mask id="mask0_7986_90880" maskUnits="userSpaceOnUse" x="0" y="-1" width="23" height="22">
            <path d="M22.7601 -0.00012207H0V20.0001H22.7601V-0.00012207Z" fill="white" />
        </mask>
        <g mask="url(#mask0_7986_90880)">
            <path
                d="M3.98962 18.7954C4.15355 18.5241 4.23121 18.2238 4.23698 17.9177C4.2571 16.8927 4.49295 15.9053 5.00493 15.0506L9.67027 7.32424C10.2542 6.34833 11.1803 5.62651 12.2791 5.18767C12.6472 5.04039 12.9809 4.8152 13.2541 4.50916C13.8582 3.83353 14.025 2.92978 13.7834 2.10116L12.3683 3.28491C12.0289 3.5679 11.5082 3.5852 11.0912 3.32826C10.7949 3.14636 10.536 2.91248 10.329 2.64108L10.2053 2.48225C9.94928 2.14736 9.96656 1.71138 10.2456 1.44864L11.6894 0.0973513C10.6827 -0.168245 9.58977 0.0973511 8.89082 0.874061C8.36157 1.46594 8.16598 2.23396 8.28968 2.97312C8.46799 4.05008 8.25514 5.11258 7.72304 6.00475L3.0692 13.864C2.56872 14.7042 1.86115 15.4173 0.966618 15.8735C0.655991 16.0323 0.39425 16.272 0.218793 16.5924C-0.2673 17.4673 0.086484 18.6482 1.01839 19.2747C2.06824 19.9763 3.4201 19.7483 3.98962 18.7954ZM1.24275 18.6655C1.20247 18.5702 1.12193 18.4894 1.02127 18.449C0.854461 18.3797 0.75091 18.2122 0.773919 18.0505C0.7883 17.9524 0.756661 17.8513 0.684754 17.7676C0.569703 17.6318 0.563949 17.4471 0.670372 17.3258C0.733652 17.2536 0.759538 17.1526 0.739402 17.0544C0.704889 16.8898 0.796931 16.7339 0.958003 16.6877C1.05582 16.6588 1.13059 16.5895 1.16509 16.5C1.22263 16.3499 1.38656 16.2691 1.55914 16.3066C1.66268 16.3297 1.76911 16.3095 1.84965 16.2518C1.98197 16.1565 2.17755 16.168 2.3156 16.2835C2.39902 16.3528 2.50833 16.3874 2.61188 16.3788C2.78446 16.3643 2.957 16.4683 3.02316 16.6271C3.06343 16.7224 3.14397 16.8032 3.24467 16.8436C3.41149 16.9129 3.51503 17.0804 3.49203 17.2421C3.47764 17.3403 3.50926 17.4413 3.58119 17.525C3.69623 17.6607 3.702 17.8455 3.59557 17.9668C3.5323 18.039 3.50641 18.14 3.52653 18.2382C3.56103 18.4028 3.46899 18.5587 3.30794 18.6049C3.21013 18.6338 3.13536 18.703 3.10086 18.7925C3.04332 18.9427 2.87935 19.0235 2.70392 18.986C2.60038 18.9629 2.49395 18.9831 2.41341 19.0409C2.28109 19.1361 2.08551 19.1246 1.94747 19.0091C1.86404 18.9398 1.75473 18.9051 1.65118 18.9138C1.48149 18.9282 1.3089 18.8272 1.24275 18.6655Z"
                fill={color}
            />
            <path
                d="M10.8011 5.13124C10.3496 5.18323 9.85772 5.05329 9.46655 4.779C9.07538 4.50471 8.79064 4.08894 8.68994 3.65295C9.10704 4.42677 9.92099 5.03019 10.8011 5.13124Z"
                fill={color}
            />
            <path
                d="M11.1384 0.249023C10.8536 0.454016 10.5861 0.673455 10.3244 0.898649C10.2582 0.953539 10.1921 1.01129 10.1288 1.06903C10.0626 1.12964 10.0022 1.17873 9.93321 1.24803C9.86705 1.31443 9.8124 1.39238 9.77216 1.47612C9.72901 1.55986 9.69735 1.64936 9.68012 1.74176C9.64558 1.92945 9.66574 2.12864 9.73474 2.31633C9.76928 2.41163 9.82105 2.49823 9.87571 2.58483C9.93609 2.67147 9.99648 2.73787 10.0569 2.81296C10.3043 3.1132 10.6178 3.36729 10.9687 3.53478C10.6609 3.30089 10.3963 3.0295 10.1863 2.72921C10.1346 2.65127 10.0742 2.57328 10.0281 2.50398C9.98498 2.42893 9.94471 2.35388 9.92171 2.27303C9.86994 2.11709 9.85555 1.94965 9.88721 1.79661C9.91309 1.64071 9.98498 1.49922 10.0885 1.38083C10.1374 1.32308 10.2064 1.25958 10.264 1.20183C10.3244 1.14119 10.3848 1.08058 10.4452 1.01704C10.681 0.768748 10.9169 0.517558 11.1384 0.249023Z"
                fill={color}
            />
            <path
                d="M13.6192 2.59033C13.6336 3.1505 13.4553 3.67598 13.1418 4.09462C12.9807 4.30251 12.7908 4.4873 12.5694 4.63454C12.4601 4.70674 12.3422 4.77313 12.2185 4.82798C12.0977 4.87998 11.9596 4.93193 11.833 4.98968C11.3182 5.22356 10.8407 5.52671 10.4323 5.89919C10.021 6.26877 9.68446 6.70761 9.44287 7.18979C9.75638 6.74805 10.1303 6.35537 10.5474 6.01758C10.9673 5.67975 11.4333 5.39391 11.9366 5.16871C12.0632 5.11097 12.1868 5.06187 12.322 5.00123C12.4543 4.94058 12.578 4.86843 12.6931 4.78758C12.9232 4.62299 13.1245 4.4209 13.2769 4.18991C13.4323 3.95892 13.5416 3.69908 13.602 3.42765C13.6566 3.15336 13.6681 2.87042 13.6192 2.59033Z"
                fill={color}
            />
            <path
                d="M0.445312 16.7237C0.643774 16.4552 0.902646 16.256 1.21039 16.1261C1.36859 16.0568 1.51529 15.9673 1.66198 15.8807C1.8058 15.7883 1.94384 15.6901 2.07615 15.5833C2.3379 15.3696 2.57375 15.1271 2.77799 14.8644C2.98219 14.6016 3.15766 14.3157 3.28709 14.0097C3.08 14.2695 2.87003 14.5208 2.64568 14.7575C2.53063 14.873 2.42132 14.9943 2.29762 15.1011C2.1797 15.2137 2.05604 15.3205 1.9295 15.4245C1.67637 15.6324 1.40597 15.82 1.11835 15.9875C0.819219 16.1521 0.56324 16.4119 0.445312 16.7237Z"
                fill={color}
            />
            <path
                d="M4.38867 19.9925C4.7108 18.7018 5.23717 17.5787 5.87285 16.4988C7.13267 14.3651 8.71461 12.4711 10.2793 10.5626C10.9869 9.69927 11.6398 8.79266 12.3157 7.90339C12.3819 7.81675 12.4452 7.73015 12.5516 7.66665C11.9217 9.21709 11.2918 10.7676 10.6417 12.3614C12.2927 12.3614 13.8775 12.3614 15.5199 12.3614C15.146 11.4461 14.7865 10.5626 14.4269 9.68197C14.0444 8.74071 13.6561 7.79945 13.2879 6.85243C13.2333 6.71094 13.2218 6.50885 13.2793 6.37311C13.587 5.60509 13.984 4.86887 14.2256 4.08351C14.4672 3.30104 14.5563 2.47238 14.7232 1.62066C14.9677 1.50806 15.0281 1.74191 15.1115 1.94115C15.8363 3.65042 16.564 5.3597 17.2917 7.06898C19.0549 11.2238 20.8181 15.3786 22.5812 19.5334C22.6359 19.6633 22.6819 19.7961 22.7567 19.9925C22.1411 19.9925 21.5832 19.9925 21.0223 19.9925C20.355 19.9925 19.6905 19.9751 19.0233 20.0011C18.7184 20.0127 18.5717 19.9232 18.4566 19.6286C18.0166 18.4911 17.5391 17.3708 17.0933 16.2361C16.9926 15.982 16.866 15.8867 16.587 15.8896C14.24 15.9012 11.8958 15.9012 9.54872 15.8896C9.28987 15.8896 9.17194 15.9762 9.07701 16.2159C8.62546 17.365 8.14798 18.5026 7.69642 19.6517C7.59572 19.9029 7.47206 20.0069 7.19305 20.0011C6.28413 19.9809 5.37236 19.9925 4.38867 19.9925Z"
                fill={color}
            />
        </g>
    </svg>
);
// const SwitchToggle = styled(Switch)(({ theme }) => ({
//     '& .MuiSwitch-switchBase.Mui-checked': {
//         color: green[500],
//         '&:hover': {
//             backgroundColor: alpha(green[600], theme.palette.action.hoverOpacity),
//         },
//     },
//     '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
//         backgroundColor: green[600],
//     },
// }));

const EmailCustomer = ({
    selectedButton,
    setSelectedButton,
    shopName,
    appointmentReview,
    setAppointmentReview,
    invoiceReviewManualSelect,
    setInvoiceReviewManualSelect,
    shouldSendReviewSMSToCustomer,
    setShouldSendReviewSMSToCustomer,
    shouldSendReviewEmailToCustomer,
    setShouldSendReviewEmailToCustomer,
    shouldSendReviewNotificationToCustomer,
    setShouldSendReviewNotificationToCustomer,

    openingMessage,
    setOpeningMessage,
    closingMessage,
    setClosingMessage,
}) => {
    // TanStack Query hooks
    const { data: emailThankYouData, isLoading: isEmailDataLoading, error: emailDataError } = useGetEmailThankYouDetails();

    const updateEmailMutation = useUpdateEmailThankYouDetails();
    const updateShopConfigMutation = useUpdateShopConfig();
    const sendTestEmailMutation = useSendTestThankYouEmail();

    // Local state
    const { isOpenSideBar } = useContext(Context);
    const editorRef = useRef(null);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState(false);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [thankuSwitch, setThankuSwitch] = useState(false);
    const [isTesting, setIsTesting] = useState(false);

    const getDefaultOpeningMessage = (name) =>
        `Thank you for choosing ${shopName} for your recent car service needs. Your satisfaction is our priority, and we take pride in delivering exceptional service every time.`;

    const getDefaultClosingMessage = () => `We truly appreciate your business and look forward to serving you again.`;

    const chipIconColor = (selected) => (selected ? '#FFF' : '#000');

    const channels = [
        { id: 'text', label: 'Text', Icon: TextIcon, size: 18, value: shouldSendReviewSMSToCustomer, setter: setShouldSendReviewSMSToCustomer },
        {
            id: 'email',
            label: 'Email',
            Icon: EmailIcon,
            size: 24,
            value: shouldSendReviewEmailToCustomer,
            setter: setShouldSendReviewEmailToCustomer,
        },
        {
            id: 'app',
            label: 'App',
            Icon: AppIcon,
            size: 24,
            value: shouldSendReviewNotificationToCustomer,
            setter: setShouldSendReviewNotificationToCustomer,
        },
    ];

    const {
        handleSubmit,
        setValue,
        control,
        formState: { errors },
    } = useForm();
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up('1280'));
    const isMobile = useMediaQuery(theme.breakpoints.up('sm'));
    function cleanHtmlContent(html) {
        // Remove unnecessary <br> tags and newlines between <p> tags
        let cleanedHtml = html
            .replace(/<pre>/g, '<p>') // Remove opening <pre> tag
            .replace(/<\/pre>/g, '</p>') // Remove closing </pre> tag
            .replace(/&#10;/g, '</p><p>') // Replace newline characters with closing and opening <p> tags

            .replace(/<p>\s*<\/p>/g, '') // Remove empty <p> tags
            .replace(/\n\s*<p>/g, '<p>') // Remove newline before <p> tags
            .replace(/<code>/g, '<code style="font-family:Roboto">');

        // Base styles to apply to all tags: margin-bottom: 0, margin-top: 0, font-size: 16px, color: #000, font-weight: 400, line-height: 24px, letter-spacing: 0.15px
        // Using !important to override browser default styles (margin-block-start: 1em)
        const baseStyles =
            'margin-bottom:0 !important;margin-top:0 !important;font-size:16px;color:#000;font-weight:400;line-height:24px;letter-spacing:0.15px;font-family:Roboto,sans-serif';

        // Apply styles to all <p> tags
        cleanedHtml = cleanedHtml.replace(/<p([^>]*)>/gi, (match, attributes) => {
            // Remove existing style attribute if present
            const withoutStyle = attributes.replace(/style\s*=\s*["'][^"']*["']/gi, '').trim();
            // Add base styles
            return `<p style="${baseStyles}"${withoutStyle ? ' ' + withoutStyle : ''}>`;
        });

        // Apply styles to other common block/inline tags (div, span, h1-h6, li, td, th, etc.)
        cleanedHtml = cleanedHtml.replace(/<(div|span|h1|h2|h3|h4|h5|h6|li|td|th|strong|b|em|i|u)([^>]*)>/gi, (match, tag, attributes) => {
            // Remove existing style attribute if present
            const withoutStyle = attributes.replace(/style\s*=\s*["'][^"']*["']/gi, '').trim();
            // Add base styles
            return `<${tag} style="${baseStyles}"${withoutStyle ? ' ' + withoutStyle : ''}>`;
        });

        // Apply styles to <code> tags (keep monospace font-family)
        cleanedHtml = cleanedHtml.replace(/<code([^>]*)>/gi, (match, attributes) => {
            const withoutStyle = attributes.replace(/style\s*=\s*["'][^"']*["']/gi, '').trim();
            const codeStyles =
                'margin-bottom:0 !important;margin-top:0 !important;font-size:16px;color:#000;font-weight:400;line-height:24px;letter-spacing:0.15px;font-family:Roboto,monospace';
            return `<code style="${codeStyles}"${withoutStyle ? ' ' + withoutStyle : ''}>`;
        });

        return cleanedHtml;
    }

    // Process email data when it's loaded
    useEffect(() => {
        if (emailThankYouData) {
            const toBool = (val) => val === true || val === 'true';

            setValue('shopEmail', emailThankYouData?.replyToEmail);
            setThankuSwitch(toBool(emailThankYouData?.isShopSendThankYouEmail));

            // Convert HTML to ContentState
            const blocksFromHTML = convertFromHTML(
                emailThankYouData?.shopThankYouEmailSignature ? cleanHtmlContent(emailThankYouData?.shopThankYouEmailSignature) : ''
            );

            // Safeguard to check if the conversion returned valid blocks
            const contentBlocks = blocksFromHTML.contentBlocks;
            let contentState =
                contentBlocks.length > 0 ? ContentState.createFromBlockArray(contentBlocks, blocksFromHTML.entityMap) : ContentState.createFromText(''); // Fallback to an empty content state

            // Apply the Roboto style to every character
            const selectionState = contentState.getSelectionAfter();
            const blockMap = contentState.getBlockMap();

            blockMap.forEach((block) => {
                const blockKey = block.getKey();
                const length = block.getLength();

                for (let i = 0; i < length; i++) {
                    const updatedSelection = selectionState.merge({
                        anchorKey: blockKey,
                        anchorOffset: i,
                        focusKey: blockKey,
                        focusOffset: i + 1,
                    });

                    contentState = Modifier.applyInlineStyle(contentState, updatedSelection, 'ROBOTO');
                }
            });
            setEditorState(EditorState.createWithContent(contentState));

            setOpeningMessage(emailThankYouData?.shopThankYouEmailCustomMessage ?? '');
            setClosingMessage(emailThankYouData?.shopThankYouEmailClosingMessage ?? '');
        }
    }, [emailThankYouData, setValue]);

    const onSubmitEmailDetails = async (values) => {
        const contentState = editorState.getCurrentContent();
        const text = contentState.getPlainText();

        if (text.trim() === '') {
            return setError(true);
        } else {
            setError(false);
        }

        let htmlContent = stateToHTML(editorState.getCurrentContent());
        htmlContent = cleanHtmlContent(htmlContent);

        try {
            // Update shop config first
            const shopConfigPayload = {
                appointmentReviewPlatform: selectedButton,
                appointmentReviewManualSelect: Boolean(appointmentReview),
                invoiceReviewManualSelect: Boolean(invoiceReviewManualSelect),
                shouldSendReviewSMSToCustomer: Boolean(shouldSendReviewSMSToCustomer),
                shouldSendReviewEmailToCustomer: Boolean(shouldSendReviewEmailToCustomer),
                shouldSendReviewNotificationToCustomer: Boolean(shouldSendReviewNotificationToCustomer),
            };
            const shopConfigResponse = await updateShopConfigMutation.mutateAsync(shopConfigPayload);

            if (shopConfigResponse?.config?.appointmentReviewPlatform && shopConfigResponse?.config?.appointmentReviewPlatform !== '') {
                setSelectedButton(shopConfigResponse.config.appointmentReviewPlatform);
            } else {
                setSelectedButton(APPOINTMENT_REVIEW_PLATFORM.GOOGLE);
            }

            let emailPayload = {
                isShopSendThankYouEmail: thankuSwitch,
                replyToEmail: values?.shopEmail,
                shopThankYouEmailSignature: htmlContent,
                shopThankYouEmailClosingMessage: closingMessage,
                shopThankYouEmailCustomMessage: openingMessage,
            };

            if (isTesting) {
                delete emailPayload.isShopSendThankYouEmail;
                emailPayload.shopThankYouEmailSignature = `${htmlContent}`;
                await sendTestEmailMutation.mutateAsync(emailPayload);
                setOpenSuccessModal(true);
                setSuccessMessage('Email Sent');
            } else {
                if (thankuSwitch === false) {
                    delete emailPayload.replyToEmail;
                    delete emailPayload.shopThankYouEmailSignature;
                }
                await updateEmailMutation.mutateAsync(emailPayload);

                queryClient.invalidateQueries({ queryKey: coreSettingsKeys.emailThankYou() });
                queryClient.invalidateQueries({ queryKey: coreSettingsKeys.config() });
                toast.success('Changes Saved');
                // setOpenSuccessModal(true);
                // setSuccessMessage('Changes Saved');
            }
        } catch (error) {
            console.error('Failed to save email settings:', error);
            // Handle error appropriately
        }
    };

    const cancelHandle = () => {
        if (emailThankYouData) {
            const toBool = (val) => val === true || val === 'true';
            setValue('shopEmail', emailThankYouData?.replyToEmail);

            const config = emailThankYouData?.config || {};
            setAppointmentReview(toBool(config.appointmentReviewManualSelect ?? emailThankYouData?.appointmentReviewManualSelect));
            setInvoiceReviewManualSelect(toBool(config.invoiceReviewManualSelect ?? emailThankYouData?.invoiceReviewManualSelect));
            setShouldSendReviewSMSToCustomer(toBool(config.shouldSendReviewSMSToCustomer ?? emailThankYouData?.shouldSendReviewSMSToCustomer));
            setShouldSendReviewEmailToCustomer(toBool(config.shouldSendReviewEmailToCustomer ?? emailThankYouData?.shouldSendReviewEmailToCustomer));
            setShouldSendReviewNotificationToCustomer(
                toBool(config.shouldSendReviewNotificationToCustomer ?? emailThankYouData?.shouldSendReviewNotificationToCustomer)
            );

            if (emailThankYouData?.shopThankYouEmailCustomMessage) {
                setOpeningMessage(emailThankYouData.shopThankYouEmailCustomMessage);
            }
            if (emailThankYouData?.shopThankYouEmailClosingMessage) {
                setClosingMessage(emailThankYouData.shopThankYouEmailClosingMessage);
            }

            // setOpeningMessage( emailThankYouData?.shopThankYouEmailCustomMessage ?? '');
            //         setClosingMessage(emailThankYouData?.shopThankYouEmailClosingMessage ?? '');

            setThankuSwitch(toBool(emailThankYouData?.isShopSendThankYouEmail));
            setEditorState(
                EditorState.createWithContent(ContentState.createFromBlockArray(convertFromHTML(emailThankYouData?.shopThankYouEmailSignature ?? '')))
            );
        }
    };

    const validateNoLeadingSpaces = (value) => {
        if (value && value.trim() !== value) {
            return 'Leading spaces are not allowed';
        }
        return true;
    };
    const StyledToggleButtonGroupLaptop = styled(ToggleButtonGroup)(({ theme }) => ({
        borderRadius: '30px',
        padding: '6px',
        background: '#E9E9EA',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        width: 'max-content',

        '& .MuiToggleButton-root': {
            border: 'none',
            color: '#FFF',
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.15px',
            fontFeatureSettings: "'liga' off, 'clig' off",
            borderRadius: '40px',
            padding: '0 12px',
            transition: 'none', // Disable hover effects
            textTransform: 'capitalize',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',

            '&.Mui-selected': {
                backgroundColor: '#006CDD',
                color: '#FFF',
            },

            '&:not(.Mui-selected)': {
                backgroundColor: '#E9E9EA',
                color: '#000',
            },

            // Custom hover effects for unselected buttons
            '&:not(.Mui-selected):hover': {
                backgroundColor: '#E9E9EA', // Change this color for unselected hover
            },

            // Custom hover effects for selected buttons
            '&.Mui-selected:hover': {
                backgroundColor: '#006CDD', // Change this color for selected hover
            },
        },
    }));

    const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
        borderRadius: '50px',
        padding: '6px',
        background: '#E9E9EA',
        display: 'flex',
        flexDirection: 'coloumn',
        alignItems: 'center',
        gap: '6px',
        width: 'max-content',

        '& .MuiToggleButton-root': {
            border: 'none',
            color: '#FFF',
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.15px',
            fontFeatureSettings: "'liga' off, 'clig' off",
            borderRadius: '40px',
            transition: 'none', // Disable hover effects
            textTransform: 'capitalize',
            height: '100%',
            display: 'flex',
            padding: '10.5px 12px',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            width: '160px',

            '&.Mui-selected': {
                backgroundColor: '#006CDD',
                color: '#FFF',
            },

            '&:not(.Mui-selected)': {
                backgroundColor: '#E9E9EA',
                color: '#000',
            },

            // Custom hover effects for unselected buttons
            '&:not(.Mui-selected):hover': {
                backgroundColor: '#E9E9EA', // Change this color for unselected hover
            },

            // Custom hover effects for selected buttons
            '&.Mui-selected:hover': {
                backgroundColor: '#006CDD', // Change this color for selected hover
            },
        },
    }));

    const ThankyouToggle = styled((props) => (
        <Switch
            focusVisibleClassName=".Mui-focusVisible"
            disableRipple
            {...props}
            defaultChecked={thankuSwitch}
            onChange={(e) => setThankuSwitch(e.target.checked)}
        />
    ))(({ theme }) => ({
        width: 42,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#92BC94',
                    opacity: 1,
                    border: 0,
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: '#33cf4d',
                border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
        },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: theme.palette.mode === 'light' ? 'rgba(120, 120, 128, 0.16)' : '#39393D',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
        },
    }));
    const handleEditorChange = (newEditorState) => {
        const contentState = newEditorState.getCurrentContent();
        const text = contentState.getPlainText();

        if (text.trim() === '') {
            setError(true);
        } else {
            setError(false);
        }
        // const rawContent = convertToRaw(newEditorState.getCurrentContent());
        // const text = rawContent.blocks.map((block) => block.text).join("");
        // console.log("text", text);

        setEditorState(newEditorState);
    };
    const blockStyleFn = (block) => {
        if (block.getType() === 'header-one') {
            return 'header-one';
        } else if (block.getType() === 'header-two') {
            return 'header-two';
        } else if (block.getType() === 'ROBOTO_BLOCK') {
            return 'roboto-block';
        } else {
            return null;
        }
    };

    // Handle pasted text to apply the Roboto style
    const handlePastedText = (text, html, editorState) => {
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();

        // Replace pasted content with plain text and apply Roboto style
        const newContentState = Modifier.replaceText(contentState, selection, text, editorState.getCurrentInlineStyle().add('ROBOTO'));

        const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');

        setEditorState(newEditorState);
        return 'handled';
    };

    console.log('🚀 ~ EmailCustomer ~ appointmentReview:', appointmentReview);
    return (
        <>
            {' '}
            <form onSubmit={handleSubmit(onSubmitEmailDetails)} style={{ marginTop: '20px' }}>
                <Grid
                    container
                    sx={{ boxShadow: 3, bgcolor: 'white' }}
                    justifyContent={'space-between'}
                    borderTop={'none'}
                    padding={'24px'}
                    borderRadius={'10px'}
                    marginTop={'32px'}
                >
                    <Grid item xs={12} sm={isOpenSideBar ? 12 : 12} md={isOpenSideBar ? 12 : 6} lg={3.8} xl={4} mb={{ xs: '24px', sm: '24px' }}>
                        <div style={{ display: 'flex' }}>
                            <Typography variant="h5">Reviews and Thank You Email</Typography>
                        </div>
                        <Stack mt={'12px'} gap={3} style={{ maxWidth: '352px' }}>
                            <div>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Direct Google Reviews
                                </Typography>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    {`Send customers straight to Google to maximize your review count. `}
                                </Typography>
                            </div>

                            <div>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Filter Reviews through Apex Auto
                                </Typography>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Customers leave reviews on Apex Auto first. Happy customers (4+ stars) get redirected to Google automatically. Unhappy ones
                                    get flagged to you privately.
                                </Typography>
                            </div>

                            <div>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Appointment Completion
                                </Typography>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Choose to send review requests automatically after every appointment, or decide manually each time.
                                </Typography>
                            </div>

                            <div>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Invoice Payment
                                </Typography>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Choose whether you want to automatically request a review from each customer with a completed invoice.
                                </Typography>
                            </div>

                            <div>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Thank You Emails
                                </Typography>
                                <Typography
                                    style={{
                                        margin: '0',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '24px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Send a personalized thank-you after every visit. A small touch that keeps your shop top of mind and brings customers back.
                                </Typography>
                            </div>

                            {/* <Typography
                                style={{
                                    margin: '0',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    lineHeight: '24px',
                                    letterSpacing: '0.15px',
                                }}
                            >
                                {`Automatically send personalized thank-you emails to customers after their visit. Enhance the customer experience with a simple gesture while encouraging feedback and building loyalty.`}
                            </Typography> */}
                        </Stack>
                    </Grid>

                    <Grid
                        item
                        container
                        xs={12}
                        sm={isOpenSideBar ? 12 : 12}
                        md={isOpenSideBar ? 12 : 6}
                        lg={7.7}
                        xl={8}
                        // mt={!isMobile ? "24px" : ""}
                        style={{ height: 'max-content' }}
                    >
                        <Grid container item xs={12} gap={3}>
                            <Grid item xs={12}>
                                {isSmUp ? (
                                    <StyledToggleButtonGroupLaptop
                                        size="small"
                                        value={selectedButton}
                                        exclusive
                                        onChange={(event, newValue) => {
                                            // Only update the selectedButton if the new value is different
                                            if (newValue && newValue !== selectedButton) {
                                                console.log('🚀 ~ value:', newValue);
                                                setSelectedButton(newValue);
                                            }
                                        }}
                                        aria-label="text alignment"
                                    >
                                        <ToggleButton
                                            value={APPOINTMENT_REVIEW_PLATFORM.GOOGLE}
                                            aria-label="centered"
                                            style={{
                                                background:
                                                    selectedButton === APPOINTMENT_REVIEW_PLATFORM.GOOGLE
                                                        ? 'linear-gradient(81deg, #1A237E 36.53%, #4280EF 63.47%)'
                                                        : '',
                                            }}
                                        >
                                            {!isSmUp ? <img src={googleImgSmall} alt="google-img" /> : <img src={googleImg} alt="google-img" />}
                                            <p style={{ margin: 0 }}>{!isSmUp ? 'Review on Google' : 'Review directly on Google'}</p>
                                        </ToggleButton>

                                        <ToggleButton value={APPOINTMENT_REVIEW_PLATFORM.APEX_AUTO} aria-label="left aligned">
                                            <FilterIcon color={selectedButton !== APPOINTMENT_REVIEW_PLATFORM.APEX_AUTO ? '#006CDD' : '#fff'} />{' '}
                                            <p style={{ margin: 0 }}>{!isSmUp ? 'Filter Review' : 'Filter reviews through Apex Auto'}</p>
                                        </ToggleButton>
                                    </StyledToggleButtonGroupLaptop>
                                ) : (
                                    <StyledToggleButtonGroup
                                        size="small"
                                        value={selectedButton}
                                        exclusive
                                        onChange={(event, newValue) => {
                                            // Only update the selectedButton if the new value is different
                                            if (newValue && newValue !== selectedButton) {
                                                console.log('🚀 ~ value:', newValue);
                                                setSelectedButton(newValue);
                                            }
                                        }}
                                        aria-label="text alignment"
                                    >
                                        <ToggleButton value={APPOINTMENT_REVIEW_PLATFORM.APEX_AUTO} aria-label="left aligned">
                                            <FilterIcon color={selectedButton !== APPOINTMENT_REVIEW_PLATFORM.APEX_AUTO ? '#006CDD' : '#fff'} />{' '}
                                            <p style={{ margin: 0 }}>{!isSmUp ? 'Filter Reviews' : 'Filter reviews through Apex Auto'}</p>
                                        </ToggleButton>
                                        <ToggleButton
                                            value={APPOINTMENT_REVIEW_PLATFORM.GOOGLE}
                                            aria-label="centered"
                                            style={{
                                                background:
                                                    selectedButton === APPOINTMENT_REVIEW_PLATFORM.GOOGLE
                                                        ? 'linear-gradient(245deg, #1A237E 34.49%, #4280EF 70.32%)'
                                                        : '',
                                            }}
                                        >
                                            {!isSmUp ? (
                                                <img src={googleImgSmall} alt="google-img-small" style={{ height: '20px' }} />
                                            ) : (
                                                <img src={googleImg} alt="google-img" />
                                            )}
                                            <p style={{ margin: 0 }}>{!isSmUp ? 'Review on Google' : 'Review directly on Google'}</p>
                                        </ToggleButton>
                                    </StyledToggleButtonGroup>
                                )}
                                <Divider sx={{ mt: 3 }} />
                            </Grid>

                            <Box sx={{ width: '100%' }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    {/* Left: icon + title */}
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M23.9837 2.42265C24.1007 1.87802 23.5667 1.41879 23.0459 1.61753L0.452326 10.2425C0.319493 10.2932 0.205157 10.383 0.124404 10.5C0.0436507 10.617 0.000275463 10.7558 1.30767e-06 10.898C-0.000272848 11.0402 0.0425669 11.1791 0.122868 11.2964C0.203169 11.4138 0.317158 11.504 0.449795 11.5552L6.79675 14.0065V21.7274C6.79674 21.8856 6.85008 22.0392 6.94817 22.1633C7.04625 22.2875 7.18334 22.3749 7.33726 22.4115C7.49118 22.4481 7.65295 22.4317 7.7964 22.365C7.93986 22.2982 8.05661 22.1851 8.1278 22.0438L10.7528 16.8347L17.1589 21.5889C17.5485 21.8781 18.1087 21.6941 18.2504 21.2294C24.2316 1.61908 23.973 2.47192 23.9837 2.42265ZM18.4065 4.89378L7.40021 12.7321L2.66457 10.9031L18.4065 4.89378ZM8.20298 13.8868L17.7967 7.05447C9.54144 15.7633 9.97259 15.3049 9.93659 15.3534C9.88311 15.4253 10.0296 15.1449 8.20298 18.7697V13.8868ZM17.1962 19.8654L11.5574 15.6806L21.7531 4.92472L17.1962 19.8654Z"
                                                fill="#006CDD"
                                            />
                                        </svg>
                                        <Typography fontWeight={400} fontSize="20px" letterSpacing={'0.15px'} lineHeight={'150%'}>
                                            Review Delivery Method
                                        </Typography>
                                    </Stack>

                                    {/* Right: chips aligned to the right */}
                                    <Stack direction="row" gap={1} alignItems="center">
                                        {channels.map(({ id, label, Icon, size, value, setter }) => {
                                            const selected = value;
                                            const color = chipIconColor(selected);
                                            return (
                                                <StyledChip
                                                    key={id}
                                                    $selected={selected}
                                                    onClick={() => setter(!value)}
                                                    icon={<Icon color={color} size={size} />}
                                                    label={label}
                                                    aria-label={`${id} request`}
                                                    clickable
                                                />
                                            );
                                        })}
                                    </Stack>
                                </Stack>
                                <Divider sx={{ mt: 3 }} />
                            </Box>

                            <Grid item xs={12}>
                                <Box>
                                    <Stack direction="row" alignItems="center" mb={3}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <g clip-path="url(#clip0_22517_81223)">
                                                    <path
                                                        d="M19.27 7.14425L20.707 5.70725L19.293 4.29275L17.7558 5.83C16.3514 4.83479 14.7127 4.22143 13 4.05V2H15V0H9.00001V2H11V4.05C9.28735 4.22143 7.64859 4.83479 6.24426 5.83L4.70701 4.29275L3.29301 5.70725L4.73001 7.14425C3.38865 8.56445 2.49265 10.3464 2.15264 12.2701C1.81263 14.1938 2.04351 16.175 2.81677 17.9689C3.59002 19.7629 4.87179 21.2911 6.50378 22.3648C8.13577 23.4385 10.0465 24.0106 12 24.0106C13.9535 24.0106 15.8642 23.4385 17.4962 22.3648C19.1282 21.2911 20.41 19.7629 21.1832 17.9689C21.9565 16.175 22.1874 14.1938 21.8474 12.2701C21.5074 10.3464 20.6114 8.56445 19.27 7.14425ZM12 22C10.4178 22 8.87104 21.5308 7.55544 20.6518C6.23985 19.7727 5.21447 18.5233 4.60897 17.0615C4.00347 15.5997 3.84504 13.9911 4.15372 12.4393C4.4624 10.8874 5.22433 9.46197 6.34315 8.34315C7.46197 7.22433 8.88744 6.4624 10.4393 6.15372C11.9911 5.84504 13.5997 6.00346 15.0615 6.60896C16.5233 7.21447 17.7727 8.23985 18.6518 9.55544C19.5308 10.871 20 12.4178 20 14C19.9976 16.121 19.154 18.1544 17.6542 19.6542C16.1545 21.154 14.121 21.9976 12 22Z"
                                                        fill="#006CDD"
                                                    />
                                                    <path
                                                        d="M12 8V14H6C6 15.1867 6.35189 16.3467 7.01118 17.3334C7.67047 18.3201 8.60754 19.0892 9.7039 19.5433C10.8003 19.9974 12.0067 20.1162 13.1705 19.8847C14.3344 19.6532 15.4035 19.0818 16.2426 18.2426C17.0818 17.4035 17.6532 16.3344 17.8847 15.1705C18.1162 14.0067 17.9974 12.8003 17.5433 11.7039C17.0892 10.6075 16.3201 9.67047 15.3334 9.01118C14.3467 8.35189 13.1867 8 12 8Z"
                                                        fill="#006CDD"
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_22517_81223">
                                                        <rect width="24" height="24" fill="white" />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                            <Typography fontWeight={400} fontSize="20px" letterSpacing={'0.15px'} lineHeight={'150%'}>
                                                Review Trigger Settings
                                            </Typography>
                                        </Stack>
                                    </Stack>

                                    <Grid container>
                                        <Grid item xs={6} pr={0}>
                                            <Typography fontWeight={500} fontSize={'16px'} mb={1.5} color={'#212121'}>
                                                Appointment Completion
                                            </Typography>

                                            <RadioGroup
                                                value={appointmentReview}
                                                onChange={(e) => {
                                                    setAppointmentReview(e.target.value === 'true');
                                                }}
                                                sx={{
                                                    '& .MuiButtonBase-root': { padding: '0!important' },
                                                    '& .MuiFormControlLabel-root': { mb: 1.5 },
                                                    '& .MuiFormControlLabel-label': { marginLeft: '8px' },
                                                }}
                                            >
                                                <FormControlLabel
                                                    value={true}
                                                    control={<Radio />}
                                                    label="Always ask before requesting a review"
                                                    sx={{ m: 0 }}
                                                />

                                                <FormControlLabel
                                                    value={false}
                                                    control={<Radio />}
                                                    sx={{ m: 0 }}
                                                    label="Send the review request automatically (no popup)"
                                                />
                                            </RadioGroup>
                                        </Grid>

                                        <Divider orientation="vertical" flexItem style={{ marginTop: '16px' }} />

                                        <Grid item xs={5.8} pl={3}>
                                            <Typography fontWeight={500} fontSize={'16px'} mb={1.5} color={'#212121'}>
                                                Invoice Payment
                                            </Typography>
                                            <RadioGroup
                                                value={invoiceReviewManualSelect}
                                                onChange={(e) => setInvoiceReviewManualSelect(e.target.value === 'true')}
                                                sx={{
                                                    '& .MuiButtonBase-root': { padding: '0!important' },
                                                    '& .MuiFormControlLabel-root': { mb: 1.5 },
                                                    '& .MuiFormControlLabel-label': { marginLeft: '8px' },
                                                }}
                                            >
                                                <FormControlLabel value={true} sx={{ m: 0 }} control={<Radio />} label="Select review request by default" />

                                                <FormControlLabel
                                                    value={false}
                                                    control={<Radio />}
                                                    sx={{ m: 0 }}
                                                    label="Leave review requests off - I'll choose every time"
                                                />
                                            </RadioGroup>
                                        </Grid>
                                    </Grid>
                                    <Divider sx={{ mt: 3 }} />
                                </Box>
                            </Grid>
                            {/*   Email Settings section  */}
                            <Box sx={{ width: '100%' }}>
                                <Typography fontWeight={400} mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M1.25 3.72368C1.25 3.18593 1.68754 2.75 2.22727 2.75H21.7727C22.3125 2.75 22.75 3.18593 22.75 3.72368V20.2763C22.75 20.8141 22.3125 21.25 21.7727 21.25H2.22727C1.68754 21.25 1.25 20.8141 1.25 20.2763V3.72368ZM3.20508 19.3026V8.6024L12.0003 13L20.796 8.60217V19.3026H3.20508Z"
                                            fill="#006CDD"
                                        />
                                    </svg>
                                    <Typography fontWeight={400} fontSize="20px" letterSpacing={'0.15px'} lineHeight={'150%'}>
                                        Email Settings
                                    </Typography>
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body1" className="label-500" mb={1}>
                                            Email Signature (From) <span style={{ color: REQUIRED_RED }}>*</span>
                                        </Typography>
                                        <div
                                            onClick={() => editorRef.current?.focus()}
                                            style={{
                                                display: 'flex',
                                                padding: '12px',
                                                alignItems: 'flex-start',
                                                gap: '12px',
                                                alignSelf: 'stretch',
                                                borderRadius: '8px',
                                                border: error ? '1px solid rgb(211,47,47)' : '1px solid rgba(0, 0, 0, 0.10)',
                                                background: '#FFF',
                                                boxShadow: '0 2px 3px rgba(0,0,0,0.06)',
                                                overflow: 'auto',
                                                cursor: 'text',
                                                minHeight: '80px',
                                            }}
                                        >
                                            <Editor
                                                ref={editorRef}
                                                editorState={editorState}
                                                onChange={handleEditorChange}
                                                blockStyleFn={blockStyleFn}
                                                readOnly={thankuSwitch === false}
                                                handlePastedText={handlePastedText}
                                                customStyleMap={{
                                                    ROBOTO: {
                                                        fontFamily: 'Roboto',
                                                        fontSize: '16px',
                                                        color: '#212121',
                                                        lineHeight: '130%',
                                                    },
                                                }}
                                            />
                                        </div>
                                        {error && (
                                            <span style={{ fontWeight: 400, fontSize: '12px', color: 'rgb(211,47,47)' }}>Email Signature is required</span>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body1" className="label-500" mb={1}>
                                            Email Customers Will Reply To <span style={{ color: REQUIRED_RED }}>*</span>
                                        </Typography>
                                        <Controller
                                            name="shopEmail"
                                            control={control}
                                            rules={{
                                                required: 'Shop Email for Thank You is required',
                                                pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email address' },
                                                validate: validateNoLeadingSpaces,
                                            }}
                                            render={({ field }) => (
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    error={!!errors.shopEmail}
                                                    helperText={errors.shopEmail ? errors.shopEmail.message : ''}
                                                    {...field}
                                                    InputLabelProps={{ shrink: field.value?.length > 0 }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                            background: '#FFF',
                                                            boxShadow: '0 2px 3px rgba(0,0,0,0.06)',

                                                            '& fieldset': {
                                                                border: '1px solid rgba(0,0,0,0.10)',
                                                            },

                                                            '&:hover fieldset': {
                                                                border: '1px solid rgba(0,0,0,0.20)',
                                                            },

                                                            '&.Mui-focused fieldset': {
                                                                border: '1px solid #006CDD',
                                                            },
                                                        },
                                                    }}
                                                    disabled={thankuSwitch === false}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider sx={{ borderColor: 'rgba(0,0,0,0.12)', width: '100%' }} />

                            {/*  THANK YOU EMAIL SECTION  */}
                            <Box sx={{ width: '100%' }}>
                                <Typography fontWeight={400} fontSize={'20px'} mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* your hand-shake SVG icon here */}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M17.7191 2.20766C17.6537 2.14183 17.576 2.08959 17.4903 2.05395C17.4047 2.01831 17.3129 1.99998 17.2201 2C17.1273 2.00003 17.0355 2.01842 16.9499 2.05409C16.8642 2.08976 16.7865 2.14201 16.7212 2.20784L14.644 4.30109C14.5065 4.43952 14.3239 4.51587 14.1302 4.51587H9.83339C9.63951 4.51587 9.45712 4.43952 9.31833 4.29997L7.27767 2.25359C7.21238 2.18806 7.13479 2.13607 7.04935 2.1006C6.96392 2.06512 6.87232 2.04687 6.77981 2.04688H6.77906C6.68642 2.04699 6.59471 2.06541 6.50921 2.10108C6.4237 2.13675 6.34609 2.18896 6.28083 2.25472L0.204094 8.37626C-0.0685306 8.65095 -0.0679681 9.09425 0.205407 9.36833L1.94822 11.1144C2.27831 11.4471 2.46014 11.8897 2.46014 12.3608C2.46014 12.9704 2.63119 13.5534 2.94905 14.0556C2.25727 14.8969 2.30194 16.1496 3.08381 16.9372C3.46228 17.3186 3.95794 17.5391 4.4895 17.5651C4.51388 18.0792 4.72041 18.586 5.10933 18.9777C5.4878 19.3591 5.98345 19.5796 6.5152 19.6056C6.53939 20.1198 6.74611 20.6266 7.13484 21.0183C7.74698 21.635 8.62533 21.7904 9.36708 21.524C9.472 21.8276 9.64413 22.1035 9.87061 22.3313C10.7054 23.1725 12.0591 23.1727 12.894 22.3313L13.1909 22.0325C13.3713 21.8508 13.5175 21.6381 13.6226 21.4046C14.453 22.1383 15.7329 22.122 16.542 21.3068C16.931 20.9151 17.1375 20.4083 17.1617 19.8942C17.6933 19.8684 18.1891 19.6477 18.5676 19.2663C18.9565 18.8746 19.163 18.3678 19.1874 17.8536C19.7188 17.8279 20.2146 17.6072 20.5931 17.226C21.3705 16.4428 21.4195 15.1993 20.7398 14.3582C21.2351 13.6603 21.5033 12.8274 21.5033 11.9527C21.5033 11.7478 21.5855 11.5475 21.7289 11.403L23.7958 9.32C24.0679 9.04592 24.0679 8.60351 23.7958 8.32939L17.7191 2.20766ZM4.08192 14.8967L5.0947 13.8764C5.16181 13.8083 5.24177 13.7542 5.32996 13.7173C5.41815 13.6804 5.51279 13.6614 5.60838 13.6614C5.70398 13.6614 5.79862 13.6804 5.88681 13.7173C5.97499 13.7542 6.05496 13.8083 6.12206 13.8764C6.40955 14.1659 6.40955 14.6368 6.12206 14.9262L5.10933 15.9466C5.04221 16.0147 4.96224 16.0687 4.87405 16.1056C4.78586 16.1425 4.69122 16.1615 4.59563 16.1615C4.50003 16.1615 4.40539 16.1425 4.3172 16.1056C4.22901 16.0687 4.14904 16.0147 4.08192 15.9466C3.79463 15.6571 3.79463 15.1862 4.08192 14.8966V14.8967ZM6.10744 16.9372L8.13291 14.8967C8.20003 14.8286 8.28002 14.7745 8.36823 14.7376C8.45644 14.7007 8.5511 14.6816 8.64673 14.6816C8.74235 14.6816 8.83702 14.7007 8.92523 14.7376C9.01344 14.7745 9.09343 14.8286 9.16055 14.8967C9.44784 15.1862 9.44784 15.6571 9.16055 15.9466C8.43853 16.6739 8.40009 16.7125 7.13484 17.9871C7.06772 18.0552 6.98773 18.1092 6.89954 18.1461C6.81134 18.183 6.7167 18.202 6.6211 18.202C6.52551 18.2019 6.43087 18.1829 6.34269 18.146C6.25451 18.1091 6.17454 18.055 6.10744 17.987C5.82014 17.6976 5.82014 17.2266 6.10744 16.9372ZM8.13291 20.0275C7.84566 19.7382 7.84566 19.2672 8.13291 18.9777L9.14569 17.9575C9.21282 17.8894 9.29281 17.8353 9.38101 17.7984C9.46922 17.7615 9.56387 17.7425 9.65948 17.7425C9.75509 17.7425 9.84975 17.7615 9.93795 17.7984C10.0262 17.8353 10.1061 17.8894 10.1733 17.9575C10.4386 18.2248 10.4584 18.6465 10.2335 18.9376C10.1716 18.9948 10.2637 18.9028 9.16055 20.0275C9.09345 20.0956 9.01347 20.1497 8.92526 20.1866C8.83705 20.2236 8.74238 20.2426 8.64675 20.2426C8.55112 20.2426 8.45645 20.2236 8.36824 20.1866C8.28003 20.1497 8.20001 20.0956 8.13291 20.0275ZM12.1929 21.0417L11.8961 21.3405C11.829 21.4086 11.7491 21.4627 11.6609 21.4996C11.5727 21.5366 11.478 21.5556 11.3824 21.5556C11.2868 21.5556 11.1922 21.5366 11.104 21.4996C11.0158 21.4627 10.9358 21.4086 10.8687 21.3405C10.6069 21.0769 10.5844 20.6629 10.7997 20.3724C11.1857 19.9822 11.1668 20.0053 11.234 19.9317C11.5211 19.706 11.9311 19.7283 12.1929 19.9919C12.4803 20.2814 12.48 20.7524 12.1929 21.0417ZM19.5952 16.2351C19.528 16.3032 19.448 16.3573 19.3598 16.3942C19.2716 16.4311 19.177 16.4501 19.0814 16.4501C18.9858 16.4501 18.8911 16.4311 18.8029 16.3942C18.7147 16.3573 18.6347 16.3032 18.5676 16.2351L17.2582 14.9161C16.9846 14.6405 16.5393 14.6389 16.2638 14.9124C15.9882 15.186 15.9865 15.6311 16.2601 15.9066L17.5696 17.2257C17.857 17.5152 17.857 17.9862 17.5696 18.2755C17.5025 18.3436 17.4225 18.3977 17.3343 18.4346C17.2461 18.4716 17.1515 18.4906 17.0558 18.4906C16.9602 18.4906 16.8656 18.4716 16.7773 18.4346C16.6891 18.3977 16.6092 18.3436 16.542 18.2755L15.2327 16.9565C14.9589 16.6808 14.5138 16.6792 14.2382 16.9527C13.9627 17.2263 13.961 17.6716 14.2346 17.9472L15.5439 19.2663C15.8314 19.5558 15.8314 20.0267 15.5439 20.316C15.4768 20.3841 15.3968 20.4382 15.3086 20.4751C15.2204 20.5121 15.1258 20.5311 15.0302 20.5311C14.9345 20.5311 14.8399 20.5121 14.7517 20.4751C14.6635 20.4382 14.5835 20.3841 14.5164 20.316C13.1379 18.9387 13.2041 18.9689 13.0653 18.8851C12.7071 18.5767 12.2634 18.398 11.7907 18.3739C11.7652 17.8617 11.5586 17.357 11.1714 16.9667C10.9727 16.7667 10.7416 16.6111 10.4902 16.5045C10.9671 15.6837 10.8566 14.6092 10.1585 13.9061C9.44287 13.1852 8.37244 13.0978 7.57425 13.5653C7.4682 13.3116 7.314 13.0808 7.12017 12.8858C6.28523 12.0446 4.93172 12.0446 4.09659 12.8858L3.98452 12.9987C3.90624 12.7952 3.86619 12.5789 3.86639 12.3608C3.86639 11.516 3.53958 10.7217 2.94483 10.1224L1.69514 8.87033L6.78089 3.7468L8.32153 5.29166C8.66578 5.63844 9.10725 5.85177 9.58425 5.90708L6.44217 9.67737C6.19369 9.97583 6.23395 10.4191 6.53222 10.6676C7.25348 11.2687 8.16572 11.5531 9.10083 11.4681C10.0358 11.3832 10.8819 10.9392 11.4838 10.2168C11.7355 9.9147 12.1591 9.82367 12.5141 9.99509C13.2387 10.3452 14.035 10.473 14.8183 10.373L19.5952 15.1852C19.8825 15.4747 19.8825 15.9456 19.5952 16.2351ZM20.7309 10.4122C20.3221 10.824 20.097 11.3712 20.097 11.9527C20.097 12.4434 19.9698 12.914 19.7321 13.3271L16.3123 9.88212C17.0114 9.4932 17.5847 8.90853 17.965 8.18384C18.1454 7.83997 18.0128 7.415 17.6689 7.23462C17.325 7.05406 16.9 7.18662 16.7197 7.5305C16.3381 8.25781 15.6831 8.7432 14.9494 8.92301C14.9172 8.92798 14.8852 8.93492 14.8538 8.94444C14.29 9.06111 13.6856 8.99937 13.1259 8.72895C12.6715 8.5086 12.1564 8.44684 11.6628 8.55354C11.1692 8.66024 10.7255 8.92925 10.4027 9.31761C10.042 9.75026 9.53442 10.0167 8.97361 10.0678C8.65481 10.0967 8.34061 10.0541 8.04909 9.94587L11.3041 6.03931C11.5592 5.9615 11.8236 5.91608 12.0902 5.91608H14.1302C14.7015 5.91608 15.2384 5.6982 15.6421 5.2917L17.2203 3.70123L22.3062 8.82467L20.7309 10.4122Z"
                                            fill="#006CDD"
                                        />
                                    </svg>
                                    Thank You Email
                                </Typography>

                                {/* Opening Message */}
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography
                                        sx={{
                                            color: '#212121',
                                            fontFamily: 'Roboto',
                                            fontSize: '16px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        Opening Greeting
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: '#D32F2F',
                                            fontFamily: 'Roboto',
                                            fontSize: '15px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '19.5px',
                                            letterSpacing: '0.46px',
                                            textTransform: 'uppercase',
                                            fontFeatureSettings: "'liga' off, 'clig' off",
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                        }}
                                        onClick={() => {
                                            setOpeningMessage('');
                                        }}
                                    >
                                        Reset to Default
                                    </Typography>
                                </Stack>

                                <TextField
                                    multiline
                                    rows={2}
                                    fullWidth
                                    value={openingMessage}
                                    onChange={(e) => setOpeningMessage(e.target.value)}
                                    placeholder={getDefaultOpeningMessage(shopName)}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiInputBase-input::placeholder': {
                                            fontWeight: 400,
                                            minHeight: '70px',
                                            lineHeight: '150%',
                                            letterSpacing: '0.15px',
                                            color: '#9E9E9E',
                                            opacity: 1,
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            padding: '12px!important',
                                            '& fieldset': {
                                                border: '1px solid rgba(0, 0, 0, 0.10)',
                                                boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06);',
                                            },
                                        },
                                    }}
                                />

                                {/* Closing Message */}
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3}>
                                    <Typography
                                        sx={{
                                            color: '#212121',
                                            fontFamily: 'Roboto',
                                            fontSize: '16px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        Closing Note
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: '#D32F2F',
                                            fontFamily: 'Roboto',
                                            fontSize: '15px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '19.5px',
                                            letterSpacing: '0.46px',
                                            textTransform: 'uppercase',
                                            fontFeatureSettings: "'liga' off, 'clig' off",
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            setClosingMessage('');
                                        }}
                                    >
                                        Reset to Default
                                    </Typography>
                                </Stack>
                                <TextField
                                    fullWidth
                                    value={closingMessage}
                                    onChange={(e) => setClosingMessage(e.target.value)}
                                    placeholder={getDefaultClosingMessage()}
                                    sx={{
                                        ...MuiTextFieldWithFocus,
                                        '& .MuiInputBase-input::placeholder': {
                                            fontWeight: 400,
                                            lineHeight: '150%',
                                            letterSpacing: '0.15px',
                                            color: '#9E9E9E',
                                            opacity: 1,
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        justifyContent={'end'}
                        // spacing={2}

                        sx={{ mt: '24px' }}
                    >
                        <Grid item>
                            <Stack spacing={3} direction="row">
                                <Button variant="outlined" color="primary" className="btn-text-size-manage" onClick={cancelHandle}>
                                    Cancel
                                </Button>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    type="submit"
                                    className="btn-text-size-manage"
                                    onClick={() => {
                                        setIsTesting(false);
                                    }}
                                >
                                    Save
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>{' '}
            </form>{' '}
            {/* Loader Dialog */}
            <Dialog
                fullScreen={false}
                open={isEmailDataLoading || updateEmailMutation.isPending || updateShopConfigMutation.isPending || sendTestEmailMutation.isPending}
            >
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </>
    );
};

export default EmailCustomer;
